/**
 * PhotoHeroCard Component
 * Square-crop photo card used as a visual hero for findings, subjectivities,
 * conditions, and exceptions. Uses the square crop from Cloudflare Images
 * (matching our onboard camera's square capture) and links to a full-size
 * gallery view of the original image.
 *
 * Badge overlays use the shared Badge component (with `overlay` mode) to
 * maintain visual consistency with filter pills and status indicators across
 * the system. PhotoDetailModal is lazy-loaded so table rows that never open
 * the gallery don't pay the import cost.
 */

import { useState, lazy, Suspense } from 'react';
import type { Photo, EvidencePhoto } from '../types/api';
import type { BadgeVariant, BadgeSize } from '../types/components';
import { cn } from '../utils/cn';
import { Badge } from './Badge';
import { AuthImage } from './AuthImage';
import { getPhotoTypeLabel } from '../hooks/usePhotos';

// Lazy-load the gallery modal — only fetched when user clicks to expand
const PhotoDetailModal = lazy(() => import('./PhotoDetailModal'));

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PhotoHeroCardProps {
  /** Single photo to display as hero */
  photo?: Photo | null;
  /** Evidence photo (simpler type from findings) */
  evidencePhoto?: EvidencePhoto | null;
  /** Direct URL for photo (when no full Photo object is available) */
  photoUrl?: string | null;
  /** Square thumbnail URL override */
  squareUrl?: string | null;
  /** Alt text for the image */
  alt?: string;
  /** Caption shown below the photo */
  caption?: string;
  /** Optional badge label (e.g. "Evidence", "GPS", severity) */
  badgeLabel?: string;
  /** Badge variant — uses shared Badge component for visual consistency */
  badgeVariant?: BadgeVariant;
  /** Size preset */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
  /** Show photo type label */
  showType?: boolean;
  /** Disable click-to-expand gallery behavior */
  disableGallery?: boolean;
  /** Click handler override (replaces gallery open) */
  onClick?: () => void;
}

export interface PhotoHeroGridProps {
  /** Array of photos to display */
  photos: (Photo | EvidencePhoto)[];
  /** Direct photo URLs (fallback when no Photo objects) */
  photoUrls?: string[];
  /** Max photos to show before "+N more" indicator */
  maxVisible?: number;
  /** Size preset for individual cards */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
  /** Label for the section */
  label?: string;
}

// ---------------------------------------------------------------------------
// Size config
// ---------------------------------------------------------------------------

const sizeConfig = {
  sm: {
    container: 'w-12 h-12',
    icon: 'w-4 h-4',
    text: 'text-[10px]',
  },
  md: {
    container: 'w-24 h-24',
    icon: 'w-6 h-6',
    text: 'text-xs',
  },
  lg: {
    container: 'w-40 h-40',
    icon: 'w-8 h-8',
    text: 'text-sm',
  },
} as const;

/** Maps card size to proportional Badge size */
const badgeSizeMap: Record<'sm' | 'md' | 'lg', BadgeSize> = {
  sm: 'xs',
  md: 'xs',
  lg: 'sm',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getSquareUrl(photo?: Photo | null, evidencePhoto?: EvidencePhoto | null, squareUrl?: string | null, photoUrl?: string | null): string {
  // Priority: explicit squareUrl > photo.urls.square > photo.thumbnailUrl > evidencePhoto.thumbnailUrl > photoUrl > fallback
  if (squareUrl) return squareUrl;
  if (photo?.urls?.square) return photo.urls.square;
  if (photo?.urls?.thumb) return photo.urls.thumb;
  if (photo?.thumbnailUrl) return photo.thumbnailUrl;
  if (evidencePhoto?.thumbnailUrl) return evidencePhoto.thumbnailUrl;
  if (photo?.url) return photo.url;
  if (evidencePhoto?.url) return evidencePhoto.url;
  if (photoUrl) return photoUrl;
  return '';
}

function getOriginalUrl(photo?: Photo | null, evidencePhoto?: EvidencePhoto | null, photoUrl?: string | null): string {
  if (photo?.urls?.original) return photo.urls.original;
  if (photo?.url) return photo.url;
  if (evidencePhoto?.url) return evidencePhoto.url;
  if (photoUrl) return photoUrl;
  return '';
}

// ---------------------------------------------------------------------------
// PhotoHeroCard - Single square photo card
// ---------------------------------------------------------------------------

export function PhotoHeroCard({
  photo,
  evidencePhoto,
  photoUrl,
  squareUrl,
  alt = 'Photo',
  caption,
  badgeLabel,
  badgeVariant = 'default',
  size = 'md',
  className,
  showType = false,
  disableGallery = false,
  onClick,
}: PhotoHeroCardProps) {
  const [showDetail, setShowDetail] = useState(false);
  const [imgError, setImgError] = useState(false);

  const config = sizeConfig[size];
  const imgSrc = getSquareUrl(photo, evidencePhoto, squareUrl, photoUrl);
  const originalSrc = getOriginalUrl(photo, evidencePhoto, photoUrl);
  const hasImage = !!imgSrc && !imgError;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (!disableGallery && (photo || originalSrc)) {
      if (photo) {
        setShowDetail(true);
      } else if (originalSrc) {
        window.open(originalSrc, '_blank');
      }
    }
  };

  const resolvedAlt = photo?.filename || alt;
  const typeLabel = showType && photo?.photoType ? getPhotoTypeLabel(photo.photoType) : null;

  return (
    <>
      <div
        className={cn(
          'group relative rounded-lg overflow-hidden bg-gray-100 flex-shrink-0',
          config.container,
          !disableGallery && 'cursor-pointer',
          className
        )}
        onClick={handleClick}
        role={!disableGallery ? 'button' : undefined}
        tabIndex={!disableGallery ? 0 : undefined}
        onKeyDown={!disableGallery ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        } : undefined}
        aria-label={!disableGallery ? `View ${resolvedAlt}` : undefined}
      >
        {hasImage ? (
          <AuthImage
            src={imgSrc}
            alt={resolvedAlt}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <svg className={config.icon} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}

        {/* Expand icon on hover */}
        {hasImage && !disableGallery && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <svg
              className={cn(config.icon, 'text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md')}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
          </div>
        )}

        {/* Badge overlay — uses shared Badge component with overlay mode */}
        {badgeLabel && hasImage && (
          <div className="absolute top-1 left-1">
            <Badge variant={badgeVariant} size={badgeSizeMap[size]} overlay>
              {badgeLabel}
            </Badge>
          </div>
        )}

        {/* GPS indicator for evidence photos */}
        {evidencePhoto?.gps && hasImage && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white flex items-center gap-0.5 px-1 py-0.5">
            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
            </svg>
            <Badge size="xs" overlay>GPS</Badge>
          </div>
        )}
      </div>

      {/* Caption (shown below for md/lg sizes) */}
      {caption && size !== 'sm' && (
        <p className={cn('mt-1 text-gray-600 truncate', config.text)} title={caption}>
          {caption}
        </p>
      )}

      {/* Type label */}
      {typeLabel && size !== 'sm' && (
        <p className={cn('text-gray-500 truncate', config.text)}>
          {typeLabel}
        </p>
      )}

      {/* Full-size gallery modal — lazy-loaded, only fetched on first expand */}
      {photo && showDetail && (
        <Suspense fallback={null}>
          <PhotoDetailModal
            photo={photo}
            isOpen={showDetail}
            onClose={() => setShowDetail(false)}
          />
        </Suspense>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// PhotoHeroGrid - Multiple square photo cards in a row
// ---------------------------------------------------------------------------

export function PhotoHeroGrid({
  photos,
  photoUrls,
  maxVisible = 4,
  size = 'sm',
  className,
  label,
}: PhotoHeroGridProps) {
  const config = sizeConfig[size];

  // Combine Photo objects and raw URLs
  const allItems: { photo?: Photo; evidencePhoto?: EvidencePhoto; url?: string }[] = [];

  for (const p of photos) {
    if ('policyId' in p) {
      allItems.push({ photo: p as Photo });
    } else {
      allItems.push({ evidencePhoto: p as EvidencePhoto });
    }
  }

  if (photoUrls) {
    for (const url of photoUrls) {
      allItems.push({ url });
    }
  }

  if (allItems.length === 0) return null;

  const visible = allItems.slice(0, maxVisible);
  const overflow = allItems.length - maxVisible;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label && (
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {label} ({allItems.length})
        </p>
      )}
      <div className="flex gap-2 items-start">
        {visible.map((item, i) => (
          <PhotoHeroCard
            key={item.photo?.id || item.evidencePhoto?.id || item.url || i}
            photo={item.photo}
            evidencePhoto={item.evidencePhoto}
            photoUrl={item.url}
            size={size}
          />
        ))}
        {overflow > 0 && (
          <div className={cn(
            'rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0',
            config.container
          )}>
            <span className={cn('text-gray-500 font-medium', config.text)}>
              +{overflow}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default PhotoHeroCard;
